import React, { useEffect, useState } from "react";
import { Gift, Save, RefreshCw, Users, Check, Clock } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { formatINR } from "../../lib/utils.fmt";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function AdminReferrals() {
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState({ rows: [], stats: {} });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    referrer_reward_inr: 50,
    referee_reward_inr: 50,
    min_order_amount_inr: 200,
    is_enabled: true,
  });

  const load = async () => {
    const [s, list] = await Promise.all([
      api.get("/api/admin/referrals/settings").then((r) => r.data),
      api.get("/api/admin/referrals/list").then((r) => r.data).catch(() => ({ rows: [], stats: {} })),
    ]);
    setSettings(s);
    setForm({
      referrer_reward_inr: s.referrer_reward_inr,
      referee_reward_inr: s.referee_reward_inr,
      min_order_amount_inr: s.min_order_amount_inr,
      is_enabled: s.is_enabled,
    });
    setData(list);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/api/admin/referrals/settings", {
        referrer_reward_inr: parseFloat(form.referrer_reward_inr),
        referee_reward_inr: parseFloat(form.referee_reward_inr),
        min_order_amount_inr: parseFloat(form.min_order_amount_inr),
        is_enabled: !!form.is_enabled,
      });
      toast.success("Referral settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <LoadingState fullHeight testId="admin-referrals-loading" />;

  const stats = data.stats || {};
  return (
    <div className="space-y-6 page-enter" data-testid="admin-referrals-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Module</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Gift className="w-7 h-7 text-brand-sky"/> Customer Referrals
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Customers refer friends with a unique code. Once the friend's first order reaching the minimum amount
          has been delivered and paid for, both wallets get topped up — once each.
        </p>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="referral-stats">
        <Stat label="Total" value={stats.total ?? 0} icon={Users}/>
        {/* in_progress = claimed and being paid this instant; still pending to the admin. */}
        <Stat label="Pending" value={(stats.pending ?? 0) + (stats.in_progress ?? 0)} icon={Clock}/>
        <Stat label="Fulfilled" value={stats.fulfilled ?? 0} icon={Check}/>
        <Stat label="Paid out" value={formatINR(stats.paid_out_inr ?? 0)} icon={Gift}/>
      </section>

      <section className="bg-white rounded-3xl border border-neutral-200 p-6 space-y-5" data-testid="referral-settings-card">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-extrabold tracking-tight">Settings</h2>
          <label className="inline-flex items-center gap-2 text-sm font-bold cursor-pointer" data-testid="referral-toggle-label">
            <input type="checkbox" checked={!!form.is_enabled}
                   onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                   className="w-5 h-5 rounded" data-testid="referral-enabled-toggle"/>
            {form.is_enabled ? "Enabled" : "Disabled"}
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Referrer reward (₹)" hint="Credited to the customer who referred">
            <Input type="number" step="1" value={form.referrer_reward_inr}
                   onChange={(e) => setForm({ ...form, referrer_reward_inr: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="referrer-reward"/>
          </Field>
          <Field label="Referee reward (₹)" hint="Credited to the newly-signed-up customer">
            <Input type="number" step="1" value={form.referee_reward_inr}
                   onChange={(e) => setForm({ ...form, referee_reward_inr: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="referee-reward"/>
          </Field>
          <Field label="Min order amount (₹)" hint="Rewards trigger once a referee's order reaches this in delivered, paid items — a cart counts as one order, a subscription adds up across its deliveries">
            <Input type="number" step="1" value={form.min_order_amount_inr}
                   onChange={(e) => setForm({ ...form, min_order_amount_inr: e.target.value })}
                   className="h-12 rounded-2xl font-mono" data-testid="min-order"/>
          </Field>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy}
                  className="h-12 px-6 rounded-2xl bg-brand-ink text-white font-extrabold"
                  data-testid="save-referrals">
            <Save className="w-4 h-4 mr-1.5"/> {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </section>

      <section data-testid="referral-list-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-lg font-extrabold tracking-tight">Recent referrals</h2>
            {(stats.total ?? 0) > data.rows.length && (
              <div className="text-xs text-neutral-500" data-testid="referrals-showing">
                Showing the latest {data.rows.length} of {stats.total}
              </div>
            )}
          </div>
          <Button onClick={load} variant="ghost" className="h-9 rounded-2xl text-xs" data-testid="refresh-referrals">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        </div>
        <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
          {data.rows.length === 0 ? (
            <EmptyState icon={Gift} size="sm" title="No referrals yet"
                        description="Customers will start showing here once they share their code."
                        testId="referrals-empty" />
          ) : (
            <table className="w-full">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">Referrer</th>
                  <th className="text-left px-5 py-3">Referee</th>
                  <th className="text-left px-5 py-3">Code</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Paid out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                    <td className="px-5 py-3">{r.referrer_name || r.referrer_id.slice(0, 8)}</td>
                    <td className="px-5 py-3">{r.referee_name || r.referee_id.slice(0, 8)}</td>
                    <td className="px-5 py-3 font-mono text-xs">{r.code}</td>
                    <td className="px-5 py-3">
                      {r.status === "fulfilled" ? (
                        <span className="px-2 py-0.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-xs font-extrabold uppercase tracking-wider">Fulfilled</span>
                      ) : r.status === "fulfilling" ? (
                        <span className="px-2 py-0.5 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-extrabold uppercase tracking-wider">Processing</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-xs font-extrabold uppercase tracking-wider">Pending</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold">
                      {r.status === "fulfilled" ? formatINR((r.referrer_credit || 0) + (r.referee_credit || 0)) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-neutral-400 mt-1">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-neutral-500">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="font-heading text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
